// Q9 S3 / S4 / S5 — VisitReport wizard orchestrator (3C architecture lock).
//
// Per ADR-016: replaces the monolithic VisitReport <form>. Each step
// navigation PATCHes the current section to the per-section endpoint
// shipped in S2. Final submit hits POST /sr/:sr_id/visit-report/submit.
//
// Q9 S5 lift of the S3 interim block: PATCH FAILURES NO LONGER BLOCK
// step navigation. Saves dispatch via useAutoSave; failures surface
// via RetryIndicator with retry-attempt counts; the carer can keep
// moving while the background queue retries (D12 lock — allow nav
// with visible retry warning). Auto-save badge (D11) reads from
// the hook's lastSavedAt map.
//
// Q9 S4 still in force:
//   - Step list comes from `visibleSteps(ctx)` (D5 client-side rules).
//   - Active medications fetched once at mount, cached for session (D2).
//   - Step-0 visit_type fallback when SR.visit_type is null at mount
//     (D4 + D6 exception).
//   - Submit sends `visible_steps` so the backend completeness check
//     scopes to revealed sections only (D19).
//
// The state machine + submit flow live in use-visit-report-wizard.ts; the
// pure derive helpers + step-vocabulary constants in
// visit-report-wizard-helpers.ts. This file is the orchestrator + its small
// presentational sub-components (StepBody / chrome / footer).

import { useMemo } from "react";
import type { JSX } from "react";
import type { MobilityStatus, VisitType } from "@hcm/core";
import { VitalsSection } from "./VitalsSection.js";
import { MobilitySection } from "./MobilitySection.js";
import { ClinicalSection } from "./ClinicalSection.js";
import { WellbeingSection } from "./WellbeingSection.js";
import { MedicationSection } from "./MedicationSection.js";
import { NotesSection } from "./NotesSection.js";
import { WizardStepIndicator, type StepDescriptor } from "./WizardStepIndicator.js";
import { WizardStepFooter } from "./WizardStepFooter.js";
import { ReviewStep } from "./ReviewStep.js";
import { VisitTypeStep } from "./VisitTypeStep.js";
import { AutoSaveBadge } from "./AutoSaveBadge.js";
import { RetryIndicator } from "./RetryIndicator.js";
import { SectionErrorBanner } from "./SectionErrorBanner.js";
import { CrossSectionErrorList, type CrossSectionError } from "./CrossSectionErrorList.js";
import { type FormState, type Action } from "../lib/visit-report-types.js";
import { type SectionFieldErrors } from "../lib/visit-report-section-validators.js";
import { type WizardStep, type SectionStep } from "../lib/visit-report-conditional-rules.js";
import { useAutoSave } from "../hooks/use-auto-save.js";
import { useFetcher } from "../hooks/useFetcher.js";
import {
  isSectionStep, deriveNavState, deriveChromeSaveState, buildSectionBus,
  useValidationViews, pendingFocusForStep, FALLBACK_FIRST_STEP, REVIEW_STEP_ID,
  type SectionAutoSaveBus, type NavState,
} from "./visit-report-wizard-helpers.js";
import { useVisitReportWizard, useActiveMedsCount } from "./use-visit-report-wizard.js";

// Re-exported so the section components keep importing it from here (the bus
// type moved to the helpers module during the size-split; this preserves the
// public surface for VitalsSection/ClinicalSection/… without touching them).
export type { SectionAutoSaveBus };

interface SectionRenderProps {
  step: WizardStep;
  form: FormState;
  dispatch: (action: Action) => void;
  isAtWanderingRisk: boolean;
  completedSteps: ReadonlySet<string>;
  onEdit: (id: WizardStep) => void;
  bus: SectionAutoSaveBus;
  /** Q9 S6 / D8 — current section's field errors (server + client union). */
  fieldErrors: SectionFieldErrors;
  /** Q9 S6 / D7 — cross-section errors shown at the review step. */
  crossSectionErrors: readonly CrossSectionError[];
  onNavigateToField: (section: SectionStep, fieldName: string) => void;
  /** Q9 S7 — field name to focus inside the current section (post-422 polish). */
  focusFieldName: string | null;
  visitTypeStepProps: {
    initialValue: VisitType | null;
    isSaving: boolean;
    onContinue: (visitType: VisitType) => void;
  };
}

function StepBody(props: SectionRenderProps): JSX.Element | null {
  const { step, form, dispatch, isAtWanderingRisk, completedSteps, onEdit, bus,
    fieldErrors, crossSectionErrors, onNavigateToField, focusFieldName, visitTypeStepProps } = props;
  switch (step) {
    case "visit-type": return <VisitTypeStep {...visitTypeStepProps} />;
    case "vitals":     return <VitalsSection f={form} dispatch={dispatch} bus={bus} fieldErrors={fieldErrors} focusFieldName={focusFieldName} />;
    case "mobility":   return <MobilitySection f={form} dispatch={dispatch} isAtWanderingRisk={isAtWanderingRisk} bus={bus} fieldErrors={fieldErrors} focusFieldName={focusFieldName} />;
    case "clinical":   return <ClinicalSection f={form} dispatch={dispatch} bus={bus} fieldErrors={fieldErrors} focusFieldName={focusFieldName} />;
    case "wellbeing":  return <WellbeingSection f={form} dispatch={dispatch} bus={bus} fieldErrors={fieldErrors} focusFieldName={focusFieldName} />;
    case "medication": return <MedicationSection f={form} dispatch={dispatch} bus={bus} fieldErrors={fieldErrors} focusFieldName={focusFieldName} />;
    case "notes":      return <NotesSection f={form} dispatch={dispatch} bus={bus} fieldErrors={fieldErrors} focusFieldName={focusFieldName} />;
    case "review":     return (
      <>
        <CrossSectionErrorList errors={crossSectionErrors} onNavigateToField={onNavigateToField} />
        <ReviewStep
          formState={form}
          completedSteps={completedSteps}
          onEdit={(id) => { onEdit(id as WizardStep); }}
        />
      </>
    );
    default: return null;
  }
}

interface Props {
  srId: string;
  isAtWanderingRisk: boolean;
  submissionChannel: "crm" | "mobile";
  onSubmitted: () => void;
  initialFormState?: FormState;
  patientId: string;
  baselineMobility: MobilityStatus | null | undefined;
  initialVisitType: VisitType | null | undefined;
}

interface MobilityOverrideToggleProps {
  visible: boolean;
  checked: boolean;
  onChange: (next: boolean) => void;
}

function MobilityOverrideToggle({ visible, checked, onChange }: MobilityOverrideToggleProps): JSX.Element | null {
  if (!visible) return null;
  return (
    <label className="text-xs text-slate-600 inline-flex items-center gap-1.5 cursor-pointer" data-testid="mobility-override-toggle">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="rounded border-slate-300 text-navy-700"
      />
      Show mobility step (one-off)
    </label>
  );
}

interface WizardChromeProps {
  descriptors: ReadonlyArray<StepDescriptor & { id: WizardStep }>;
  currentStep: WizardStep;
  completedSteps: ReadonlySet<string>;
  /** Q9 S6 — drives the yellow incomplete-dot per D9 9b. */
  incompleteSteps: ReadonlySet<string>;
  onStepClick: (id: WizardStep) => void;
  showMobilityOverrideToggle: boolean;
  overrideShowMobility: boolean;
  setOverrideShowMobility: (v: boolean) => void;
  lastSavedAt: Date | null;
  isSaving: boolean;
  hasFailedSections: boolean;
}

function WizardChrome(props: WizardChromeProps): JSX.Element {
  const { descriptors, currentStep, completedSteps, incompleteSteps, onStepClick,
    showMobilityOverrideToggle, overrideShowMobility, setOverrideShowMobility,
    lastSavedAt, isSaving, hasFailedSections } = props;
  return (
    <div className="flex items-center justify-between gap-3 mb-2">
      <div className="flex-1 min-w-0">
        <WizardStepIndicator
          steps={descriptors}
          currentStep={currentStep}
          completedSteps={completedSteps}
          incompleteSteps={incompleteSteps}
          onStepClick={(id) => onStepClick(id as WizardStep)}
        />
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <MobilityOverrideToggle
          visible={showMobilityOverrideToggle}
          checked={overrideShowMobility}
          onChange={setOverrideShowMobility}
        />
        <AutoSaveBadge
          lastSavedAt={lastSavedAt}
          isSaving={isSaving}
          hasFailedSections={hasFailedSections}
        />
      </div>
    </div>
  );
}

interface WizardFooterProps {
  steps: readonly WizardStep[]; nav: NavState; isSaving: boolean;
  handleStepNav: (target: WizardStep) => void;
  handleSubmit: () => Promise<void>;
}

function WizardFooter({ steps, nav, isSaving, handleStepNav, handleSubmit }: WizardFooterProps): JSX.Element {
  return (
    <WizardStepFooter
      isReviewStep={nav.isReviewStep} isFirstStep={nav.isFirstStep}
      isSaving={isSaving} submitDisabledReason={nav.submitDisabledReason}
      onPrev={() => { handleStepNav(steps[nav.currentIdx - 1] ?? steps[0] ?? FALLBACK_FIRST_STEP); }}
      onNext={() => { handleStepNav(steps[nav.currentIdx + 1] ?? REVIEW_STEP_ID); }}
      onSubmit={() => { void handleSubmit(); }}
    />
  );
}

export function VisitReportWizard({
  srId, isAtWanderingRisk, submissionChannel, onSubmitted, initialFormState,
  patientId, baselineMobility, initialVisitType,
}: Props): JSX.Element {
  // Carer-parity S3a / Sub-5 5a — full coverage. The wizard reads the active
  // BearerProvider's fetcher once; meds query, auto-save PATCHes, visit-type
  // PATCH, and final SUBMIT all route through it. Coordinator subtree → apiFetch
  // (sessionStorage bearer); carer subtree → carerFetch (localStorage bearer +
  // silent refresh on 401 via /auth/mobile-refresh — critical for the 5–15 min
  // mobile fill window).
  const fetcher = useFetcher();
  const meds = useActiveMedsCount(patientId, fetcher);
  const autoSave = useAutoSave(srId, { fetcher });
  const engine = useVisitReportWizard(
    srId, submissionChannel, onSubmitted, initialFormState,
    baselineMobility, initialVisitType, meds, autoSave.dispatchSave, fetcher,
  );
  const { form, dispatch, currentStep, steps, completedSteps, serverError, isSaving,
    visitType, overrideShowMobility, setOverrideShowMobility, currentSectionPayload,
    handleStepNav, handleSubmit, handleVisitTypePicked, serverFieldErrors, setCurrentStep,
    pendingFocus, requestFocus } = engine;

  const views = useValidationViews(steps, currentStep, form, serverFieldErrors);
  const nav = deriveNavState({ steps, currentStep, completedSteps, baselineMobility,
    incompleteSteps: views.incompleteSteps });
  const bus = useMemo<SectionAutoSaveBus>(
    () => buildSectionBus(currentStep, autoSave, currentSectionPayload),
    [currentStep, autoSave, currentSectionPayload],
  );

  if (meds.loading) {
    return <p className="p-4 text-sm text-slate-400" aria-busy="true">Loading visit context…</p>;
  }

  const chrome = deriveChromeSaveState(autoSave.status, currentStep, isSaving);
  const focusForCurrent = pendingFocusForStep(pendingFocus, currentStep);
  const onNavigateToField = (section: SectionStep, field: string): void => {
    setCurrentStep(section); requestFocus(section, field);
  };

  return (
    <div className="visit-report-wizard">
      <WizardChrome
        descriptors={nav.descriptors} currentStep={currentStep}
        completedSteps={completedSteps} incompleteSteps={views.incompleteSteps}
        onStepClick={handleStepNav}
        showMobilityOverrideToggle={nav.showMobilityOverrideToggle}
        overrideShowMobility={overrideShowMobility} setOverrideShowMobility={setOverrideShowMobility}
        lastSavedAt={chrome.lastSavedAt} isSaving={chrome.isSaving}
        hasFailedSections={chrome.hasFailedSections}
      />

      <RetryIndicator
        failedSections={autoSave.status.failedSections}
        onManualRetry={(s) => { void autoSave.manualRetry(s, currentSectionPayload()); }}
      />

      {isSectionStep(currentStep) && (
        <SectionErrorBanner
          fieldErrorCount={views.currentValidation.fieldErrors.size}
          formErrors={views.currentValidation.formErrors}
        />
      )}

      {serverError && (
        <div role="alert" className="mb-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">
          {serverError}
        </div>
      )}

      <div data-testid={`wizard-step-${currentStep}`}>
        <StepBody
          step={currentStep} form={form} dispatch={dispatch}
          isAtWanderingRisk={isAtWanderingRisk} completedSteps={completedSteps}
          onEdit={(id) => { handleStepNav(id); }} bus={bus}
          fieldErrors={views.currentValidation.fieldErrors}
          crossSectionErrors={views.crossSectionErrors}
          onNavigateToField={onNavigateToField}
          focusFieldName={focusForCurrent}
          visitTypeStepProps={{
            initialValue: visitType, isSaving,
            onContinue: (vt) => { void handleVisitTypePicked(vt); },
          }}
        />
      </div>

      {!nav.isVisitTypeStep && (
        <WizardFooter steps={steps} nav={nav} isSaving={isSaving}
          handleStepNav={handleStepNav} handleSubmit={handleSubmit} />
      )}
    </div>
  );
}
